import React from "react";
import dayjs from "dayjs";
import { Box, Text, Stack, Icon } from "@chakra-ui/react";
import {
    MdAccountBalanceWallet,
    MdAttachMoney,
    MdCalendarToday,
    MdTrendingUp
} from "react-icons/md";
import InfoRow from "../shared/InfoRow";
import FrozenBanner from "./FrozenBannerDisplay";

/**
 * Renders a placeholder when no wallet data is available.
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function WalletEmptyState({ isMobile }) {
    return (
        <Box
            textAlign="center"
            padding={isMobile ? 8 : 12}
            background="rgba(255, 255, 255, 0.02)"
            borderRadius="8px"
            border="1px solid rgba(255, 255, 255, 0.05)"
        >
            <Icon as={MdAccountBalanceWallet} boxSize={isMobile ? "48px" : "64px"} color="rgba(255, 255, 255, 0.2)" mb={4} />
            <Text color="rgba(255, 255, 255, 0.5)" fontSize={isMobile ? "0.85rem" : "0.95rem"} mb={2}>
                No wallet
            </Text>
            <Text color="rgba(255, 255, 255, 0.4)" fontSize={isMobile ? "0.75rem" : "0.8rem"}>
                The user does not have a configured wallet yet
            </Text>
        </Box>
    );
}

/**
 * Renders the wallet section header with icon and title.
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function WalletHeader({ isMobile }) {
    return (
        <Box mb={6}>
            <Stack direction="row" spacing={3} alignItems="center">
                <Icon as={MdAccountBalanceWallet} color="#17dcb8" boxSize={isMobile ? "24px" : "28px"} />
                <Text color="rgba(255, 255, 255, 0.9)" fontSize={isMobile ? "1rem" : "1.1rem"} fontWeight={600} letterSpacing="0.3px">
                    Wallet
                </Text>
            </Stack>
        </Box>
    );
}

/**
 * Renders the "Available balance" label row with icon.
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function WalletBalanceLabel({ isMobile }) {
    return (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" mb={2}>
            <Icon as={MdTrendingUp} color="#17dcb8" boxSize={isMobile ? "20px" : "24px"} />
            <Text
                color="rgba(255, 255, 255, 0.6)"
                fontSize={isMobile ? "0.75rem" : "0.8rem"}
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing="1px"
            >
                Available balance
            </Text>
        </Stack>
    );
}

/**
 * Renders the wallet balance display with currency symbol.
 * @param {Object} props
 * @param {Object} props.wallet - Wallet data with balance and currency
 * @param {string} props.currency - Resolved currency symbol
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function WalletBalance({ wallet, currency, isMobile }) {
    return (
        <Box
            mb={6}
            padding={isMobile ? 6 : 8}
            background="linear-gradient(135deg, rgba(23, 220, 184, 0.15) 0%, rgba(23, 220, 184, 0.05) 100%)"
            borderRadius="12px"
            border="1px solid rgba(23, 220, 184, 0.2)"
            textAlign="center"
        >
            <WalletBalanceLabel isMobile={isMobile} />
            <Text color="#17dcb8" fontSize={isMobile ? "2rem" : "2.5rem"} fontWeight={700} lineHeight={1} mb={1}>
                {wallet?.balance?.toFixed(2) || "0.00"}
            </Text>
            <Text color="rgba(23, 220, 184, 0.8)" fontSize={isMobile ? "0.9rem" : "1rem"} fontWeight={600}>
                {currency || wallet?.currency}
            </Text>
        </Box>
    );
}

/**
 * Renders the wallet creation and modification dates.
 * @param {Object} props
 * @param {Object} props.wallet - Wallet data with created_at and modified_at
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function WalletHistory({ wallet, isMobile }) {
    return (
        <Box
            padding={isMobile ? 4 : 5}
            background="rgba(255, 255, 255, 0.02)"
            borderRadius="8px"
            border="1px solid rgba(255, 255, 255, 0.05)"
        >
            <Text
                color="rgba(255, 255, 255, 0.7)"
                fontSize={isMobile ? "0.8rem" : "0.85rem"}
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing="0.5px"
                mb={4}
            >
                History
            </Text>
            <InfoRow icon={MdCalendarToday} label="Created on" value={dayjs(wallet?.created_at).format("DD/MM/YYYY")} layout="grid" isMobile={isMobile} />
            <InfoRow icon={MdCalendarToday} label="Modified on" value={dayjs(wallet?.modified_at).format("DD/MM/YYYY")} layout="grid" isMobile={isMobile} />
        </Box>
    );
}

/**
 * Renders a footnote about the wallet balance meaning.
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function WalletFooterNote({ isMobile }) {
    return (
        <Box
            mt={4}
            padding={isMobile ? "10px 16px" : "12px 16px"}
            background="rgba(255, 255, 255, 0.02)"
            borderRadius="8px"
            border="1px solid rgba(255, 255, 255, 0.05)"
            display="flex"
            alignItems="center"
            gap={2}
        >
            <Icon as={MdAttachMoney} color="rgba(255, 255, 255, 0.5)" boxSize="18px" />
            <Text color="rgba(255, 255, 255, 0.6)" fontSize={isMobile ? "0.7rem" : "0.75rem"} fontStyle="italic">
                The balance represents funds available for the user
            </Text>
        </Box>
    );
}

export { WalletEmptyState, WalletHeader, FrozenBanner, WalletBalance, WalletHistory, WalletFooterNote };
